import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import SimpleDialog from '../../common/SimpleDialog';
import DOMPurify from 'dompurify';
import { PlaybookStore, saveTemplate } from '../PlaybookStore';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/SaveAlt';
import isEmpty from 'lodash/isEmpty';
import PageHeader from '../../../materio/@core/components/page-header';
import WysiwygEditor, { TokenType } from '../../common2/Templating/WysiwygEditor';
import AIReviewDialog from '../../AI/reviewer/components/AIReviewDialog';
import AISentimentDialog from '../../AI/sentiment-analysis/components/AISentimentDialog';
import AISummarizeTextDialog from '../../AI/summarize-text/components/AISummarizeTextDialog';
import AIAssistButton from '../../AI/AIAssistButton';
import AIContentCreatorDialog from '../../AI/content-creator/components/AIContentCreatorDialog';
import AIReviewContentDialog from '../../AI/suggest-changes/components/AIReviewContentDialog';
import ContentEditor from '../../../v2/components/ContentEditor/ContentEditor';

type Props = {
    open: boolean;
    onClose: () => void;
    viewMode: boolean;
};

function createMarkup(html: string) {
    return {
        __html: DOMPurify.sanitize(html),
    };
}

const EditCadenceTemplate = ({ open, onClose, viewMode }: Props) => {
    const template = PlaybookStore.useState((s) => s.selectedTemplate);
    const playbook = PlaybookStore.useState((s) => s.selectedPlaybook);
    const [editorState, setEditorState] = useState(template ? template.templateBody : '');
    const [nameError, setNameError] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (!template) return;
        setNameError(false);
    }, [template]);

    if (!playbook || !template) return null;

    const onSave = async () => {
        setNameError(false);

        if (isEmpty(template.name)) {
            setNameError(true);
            return;
        }

        if (!template) return;

        const newRecord = { ...template, templateBody: editorState };

        const response = await saveTemplate(newRecord, playbook, '');

        if (response.succeeded) {
            onClose();
        } else {
            alert('error saving content');
        }
    };

    return (
        <SimpleDialog open={open} onClose={onClose} sx={{ height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', gap: 5, height: '100%' }}>
                <Box>
                    {viewMode && <PageHeader title={<Typography variant="h5">Preview</Typography>} />}

                    {!viewMode && <PageHeader title={<Typography variant="h5">Edit Template</Typography>} />}
                </Box>

                <FormGroup sx={{ mb: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        value={template.name ?? ''}
                        label="Template Name"
                        onChange={(e) =>
                            PlaybookStore.update((s, o) => {
                                s.selectedTemplate = { ...template, name: e.target.value, templateBody: editorState };
                            })
                        }
                        placeholder={`Enter Name`}
                        disabled={viewMode}
                    />
                    {nameError && (
                        <Box sx={{ color: theme.palette.error.main }}>Please enter a name for the library.</Box>
                    )}
                </FormGroup>

                {template.templateType.toLowerCase().includes('email') && (
                    <FormGroup sx={{ mb: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            value={template.subject ?? ''}
                            label="Subject"
                            onChange={(e) =>
                                PlaybookStore.update((s, o) => {
                                    s.selectedTemplate = {
                                        ...template,
                                        subject: e.target.value,
                                        templateBody: editorState,
                                    };
                                })
                            }
                            placeholder={`Enter Subject`}
                            disabled={viewMode}
                        />
                    </FormGroup>
                )}

                {template.templateType.toLowerCase().includes('email') && (
                    <FormGroup sx={{ mb: 1 }}>
                        <FormControl size="small" disabled={viewMode}>
                            <InputLabel id="status-label">Deliver To</InputLabel>
                            <Select
                                label="Deliver To"
                                defaultValue={template.deliverTo}
                                value={template.deliverTo}
                                onChange={(e) =>
                                    PlaybookStore.update((s, o) => {
                                        s.selectedTemplate = {
                                            ...template,
                                            deliverTo: e.target.value,
                                            templateBody: editorState,
                                        };
                                    })
                                }
                            >
                                <MenuItem value="lead">Lead</MenuItem>
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="compliance">Compliance</MenuItem>
                            </Select>
                        </FormControl>
                    </FormGroup>
                )}

                {template.deliverTo.toLowerCase().includes('compliance') && (
                    <FormGroup sx={{ mb: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            value={template.complianceEmail ?? ''}
                            label="Compliance Email"
                            onChange={(e) =>
                                PlaybookStore.update((s, o) => {
                                    s.selectedTemplate = {
                                        ...template,
                                        complianceEmail: e.target.value,
                                        templateBody: editorState,
                                    };
                                })
                            }
                            placeholder={`Compliance Email`}
                            disabled={viewMode}
                        />
                    </FormGroup>
                )}

                <FormControl size="small" disabled={viewMode}>
                    {/*<InputLabel id='status-label'>Status</InputLabel>*/}
                    {/*<Select*/}
                    {/*    label='Status'*/}
                    {/*    defaultValue={template.isActive ? "active" : "inactive"}*/}
                    {/*    value={template.isActive ? "active" : "inactive"}*/}
                    {/*    id='view-status'*/}
                    {/*    labelId='status-label'*/}
                    {/*    onChange={(e) => PlaybookStore.update((s, o) => { s.selectedTemplate = { ...template, isActive: e.target.value === 'active', templateBody: editorState } })}*/}
                    {/*>*/}
                    {/*    <MenuItem value='active'>Active</MenuItem>*/}
                    {/*    <MenuItem value='inactive'>Inactive</MenuItem>*/}
                    {/*</Select>*/}
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={template.allowSelection}
                                    onChange={(e) =>
                                        PlaybookStore.update((s, o) => {
                                            s.selectedTemplate = {
                                                ...template,
                                                allowSelection: e.target.checked,
                                                templateBody: editorState,
                                            };
                                        })
                                    }
                                />
                            }
                            label="Allow Selection"
                        />
                    </FormGroup>
                </FormControl>

                {/*{!viewMode && <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflowY: 'auto' }}><WysiwygEditor*/}
                {/*    html={template.templateBody}*/}
                {/*    onChange={setEditorState}*/}
                {/*    tokenType={TokenType.Lead}*/}
                {/*    textMode={template.templateType.toLowerCase().includes('sms') || template.templateType.toLowerCase().includes('social')}*/}
                {/*/></Box>}*/}

                {!viewMode && (
                    <ContentEditor
                        html={template.templateBody}
                        onChange={setEditorState}
                        tokenType={TokenType.Lead}
                        textMode={template.templateType.toLowerCase().includes('sms')}
                    />
                )}

                {viewMode && (
                    <Box>
                        <PageHeader
                            title=""
                            subtitle={
                                <Typography variant="body2">
                                    This is how the template will appear to the user.
                                </Typography>
                            }
                        />
                    </Box>
                )}

                {viewMode && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1 1 auto',
                            overflowY: 'auto',
                            border: `1px solid ${theme.palette.divider}`,
                            margin: 0,
                        }}
                    >
                        <div dangerouslySetInnerHTML={createMarkup(template.templateBody)}></div>
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 5 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {!viewMode && (
                            <AIAssistButton>
                                <MenuItem>
                                    <AIContentCreatorDialog
                                        contentType="SalesPitch"
                                        onAccept={(updatedText: string) =>
                                            PlaybookStore.update((s) => {
                                                if (s.selectedTemplate) {
                                                    s.selectedTemplate.templateBody = updatedText;
                                                }
                                            })
                                        }
                                    />
                                </MenuItem>

                                <MenuItem>
                                    <AIReviewContentDialog
                                        contentType="SalesPitch"
                                        text={editorState}
                                        onAccept={(updatedText: string) =>
                                            PlaybookStore.update((s) => {
                                                if (s.selectedTemplate) {
                                                    s.selectedTemplate.templateBody = updatedText;
                                                }
                                            })
                                        }
                                    />
                                </MenuItem>

                                <MenuItem>
                                    <AIReviewDialog
                                        contentType="SalesPitch"
                                        initialDetails={editorState}
                                        onAccept={(updatedText: string) =>
                                            PlaybookStore.update((s) => {
                                                if (s.selectedTemplate) {
                                                    s.selectedTemplate.templateBody = updatedText;
                                                }
                                            })
                                        }
                                    />
                                </MenuItem>

                                <MenuItem>
                                    <AISentimentDialog initialDetails={editorState} contentType="SalesPitch" />
                                </MenuItem>
                                <MenuItem>
                                    <AISummarizeTextDialog initialDetails={editorState} contentType="SalesPitch" />
                                </MenuItem>
                            </AIAssistButton>
                        )}
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {!viewMode && (
                                <Button
                                    sx={{ mr: 2 }}
                                    variant="contained"
                                    size="small"
                                    endIcon={<SaveIcon />}
                                    onClick={onSave}
                                >
                                    Save
                                </Button>
                            )}
                            <Button variant="contained" size="small" endIcon={<CloseIcon />} onClick={onClose}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </SimpleDialog>
    );
};

export default EditCadenceTemplate;
